import { useNavigate } from 'react-router-dom';
import { X, UserPlus, Lock, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import './AddParentModal.css';

/**
 * Modal shown when viewer-only users try to access restricted features.
 * Shows different content based on whether user already has a parent linked.
 *
 * Usage:
 * const [showModal, setShowModal] = useState(false);
 * <AddParentModal isOpen={showModal} onClose={() => setShowModal(false)} feature="trading" />
 */
const AddParentModal = ({ isOpen, onClose, feature = 'this feature' }) => {
  const navigate = useNavigate();
  const { theme, isDark } = useTheme();
  const { linkedParents, activeProfileId, userProfile } = useProfile();

  // Child has parent if:
  // 1. They have linkedParents (logged in as kid directly), OR
  // 2. A parent is viewing as them (activeProfileId is set and user is a parent)
  const hasParent = (linkedParents && linkedParents.length > 0) ||
                    (activeProfileId && userProfile?.is_parent);

  const handleGoToProfile = () => {
    onClose();
    navigate('/profile');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="add-parent-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{ background: theme.overlay }}
        >
          <motion.div
            className="add-parent-modal"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{ background: theme.bgCard }}
          >
            <button
              className="add-parent-modal-close"
              onClick={onClose}
              style={{ color: theme.textSecondary }}
            >
              <X size={20} />
            </button>

            <div className="add-parent-modal-icon">
              <Lock size={32} />
            </div>

            <h2 className="add-parent-modal-title" style={{ color: theme.text }}>
              Parent Permission Required
            </h2>

            {hasParent ? (
              <>
                <p className="add-parent-modal-text" style={{ color: theme.text }}>
                  To unlock {feature}, ask your parent to enable trading for your account.
                </p>

                <p className="add-parent-modal-subtext" style={{ color: theme.textMuted }}>
                  Your parent can update your permissions from their profile settings.
                </p>

                <div className="add-parent-modal-actions">
                  <button className="add-parent-modal-btn primary" onClick={onClose}>
                    <MessageCircle size={18} />
                    Got It
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="add-parent-modal-text" style={{ color: theme.text }}>
                  To unlock {feature}, you need a parent to approve your account.
                </p>

                <p className="add-parent-modal-subtext" style={{ color: theme.textMuted }}>
                  Add your parent's email in your Profile and they'll receive an invite to connect.
                </p>

                <div className="add-parent-modal-actions">
                  <button className="add-parent-modal-btn primary" onClick={handleGoToProfile}>
                    <UserPlus size={18} />
                    Add Parent
                  </button>
                  <button
                    className="add-parent-modal-btn secondary"
                    onClick={onClose}
                    style={{
                      background: theme.bgSheet,
                      color: theme.textMuted
                    }}
                  >
                    Maybe Later
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddParentModal;
